<template>
  <div class="notification-center">
    <!-- 通知按钮 -->
    <el-badge :value="unreadCount" :hidden="unreadCount === 0" class="notification-badge">
      <el-button circle class="notification-btn" @click="openNotificationCenter">
        <el-icon><Bell /></el-icon>
      </el-button>
    </el-badge>
    
    <!-- 通知中心弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      title="通知中心"
      width="500px"
      class="notification-dialog"
      center
      destroy-on-close
      :append-to-body="true"
    >
      <div class="notification-content">
        <div class="notification-header">
          <span>共 {{ totalNotifications }} 条通知</span>
          <el-button type="primary" plain size="small" @click="markAllAsRead" :disabled="unreadCount === 0">
            全部标为已读
          </el-button>
        </div>
        
        <el-divider />
        
        <div v-if="loading" class="notification-loading">
          <el-icon class="loading-icon"><Loading /></el-icon>
          <span>加载通知中...</span>
        </div>
        
        <div v-else-if="notifications.length === 0" class="notification-empty">
          <el-empty description="暂无通知" />
        </div>
        
        <div v-else class="notification-list">
          <div 
            v-for="notification in notifications" 
            :key="notification.id"
            class="notification-item"
            :class="{ 'notification-unread': !notification.is_read }"
            @click="viewNotificationDetail(notification)"
          >
            <div class="notification-content">
              <div class="notification-title">
                <el-tag v-if="!notification.is_read" size="small" type="danger" class="unread-tag">未读</el-tag>
                {{ notification.title }}
              </div>
              <div class="notification-time">{{ formatTime(notification.created_at) }}</div>
            </div>
          </div>
        </div>
        
        <!-- 分页控件 -->
        <div class="pagination-container" v-if="notifications.length > 0">
          <el-pagination
            background
            layout="prev, pager, next"
            :total="totalNotifications"
            :page-size="pageSize"
            v-model:current-page="currentPage"
            @current-change="handlePageChange"
          />
        </div>
      </div>
    </el-dialog>
    
    <!-- 通知详情对话框 -->
    <NotificationDetail
      v-if="selectedNotification"
      v-model:visible="detailDialogVisible"
      :notification="selectedNotification"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue';
import { Bell, Loading } from '@element-plus/icons-vue';
import { useRouter } from 'vue-router';
import { ElMessageBox, ElMessage } from 'element-plus';
import NotificationDetail from './NotificationDetail.vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';

dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:visible', 'notification-selected']);

const router = useRouter();

// 状态变量
const dialogVisible = ref(false);
const notifications = ref([]);
const loading = ref(false);
const totalNotifications = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);
const selectedNotification = ref(null);
const detailDialogVisible = ref(false);

// 计算未读通知数量
const unreadCount = computed(() => {
  return notifications.value.filter(n => !n.is_read).length;
});

// 检查用户是否登录
const isUserLoggedIn = () => {
  const token = localStorage.getItem('token');
  return !!token; // 如果token存在则返回true
};

// 打开通知中心
const openNotificationCenter = async () => {
  if (!isUserLoggedIn()) {
    // 用户未登录，提示登录
    ElMessageBox.confirm('请先登录以查看您的通知', '提示', {
      confirmButtonText: '去登录',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(() => {
      router.push('/login');
    }).catch(() => {});
    return;
  }
  
  dialogVisible.value = true;
  await fetchNotifications();
};

// 获取通知列表
const fetchNotifications = async () => {
  try {
    loading.value = true;
    const token = localStorage.getItem('token');
    if (!token) {
      ElMessage.error('未找到认证令牌，请重新登录');
      return;
    }

    console.log('开始获取通知，页码:', currentPage.value, '每页数量:', pageSize.value);
    
    const response = await fetch(`/api/notifications?page=${currentPage.value}&limit=${pageSize.value}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    console.log('API响应状态:', response.status);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('API错误响应:', errorData);
      throw new Error(errorData.message || '获取通知失败');
    }

    const data = await response.json();
    console.log('获取通知成功:', data);
    
    notifications.value = data.data || [];
    totalNotifications.value = data.total || notifications.value.length;
  } catch (error) {
    console.error('获取通知出错:', error);
    ElMessage.error(`获取通知失败: ${error.message}`);
    
    // 测试用数据 - 仅在开发环境使用
    if (import.meta.env.DEV) {
      notifications.value = [
        {
          id: 999,
          title: '测试通知',
          content: '这是一条测试通知内容',
          created_at: new Date().toISOString(),
          type: 'system',
          is_read: 0
        }
      ];
    }
  } finally {
    loading.value = false;
  }
};

// 标记通知为已读
const markAsRead = async (notification) => {
  try {
    if (notification.is_read) return;
    
    const token = localStorage.getItem('token');
    if (!token) {
      ElMessage.error('未找到认证令牌，请重新登录');
      return;
    }

    const response = await fetch(`/api/notifications/${notification.id}/read`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('标记通知已读失败');
    }

    // 更新本地通知状态
    const index = notifications.value.findIndex(n => n.id === notification.id);
    if (index !== -1) {
      notifications.value[index].is_read = 1;
    }
  } catch (error) {
    console.error('标记通知已读出错:', error);
  }
};

// 查看通知详情
const viewNotificationDetail = async (notification) => {
  // 标记为已读
  await markAsRead(notification);
  
  // 显示详情对话框
  selectedNotification.value = notification;
  detailDialogVisible.value = true;
};

// 标记所有通知为已读
const markAllAsRead = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      ElMessage.error('未找到认证令牌，请重新登录');
      return;
    }

    const response = await fetch('/api/notifications/read-all', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('标记所有通知已读失败');
    }

    // 更新所有通知状态
    notifications.value.forEach(notification => {
      notification.is_read = 1;
    });
    
    ElMessage.success('所有通知已标记为已读');
  } catch (error) {
    console.error('标记所有通知已读出错:', error);
    ElMessage.error('操作失败，请稍后重试');
  }
};

// 处理页面变化
const handlePageChange = (newPage) => {
  currentPage.value = newPage;
  fetchNotifications();
};

// 格式化时间
const formatTime = (time) => {
  return dayjs(time).format('YYYY-MM-DD HH:mm');
};

// 初始化时获取通知 - 修改为组件挂载时就获取通知，而不是等待点击
onMounted(() => {
  if (isUserLoggedIn()) {
    fetchNotifications();
  }
});

// 添加定时刷新逻辑
onMounted(() => {
  // 每3分钟自动刷新通知
  const refreshInterval = setInterval(() => {
    if (isUserLoggedIn()) {
      fetchNotifications();
    }
  }, 3 * 60 * 1000);
  
  // 组件卸载时清除定时器
  onBeforeUnmount(() => {
    clearInterval(refreshInterval);
  });
});
</script>

<style scoped>
.notification-center {
  display: inline-block;
}

.notification-badge {
  margin-right: 16px;
}

.notification-btn {
  font-size: 18px;
}

/* 修复弹窗位置 - 确保在页面中央显示 */
:deep(.el-dialog) {
  display: flex;
  flex-direction: column;
  margin: 0 !important;
  position: absolute !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  max-height: 80vh !important;
}

:deep(.el-dialog__body) {
  overflow-y: auto;
  flex: 1;
}

/* 确保详情弹窗居中显示 */
:deep(.notification-dialog), 
:deep(.notification-detail-dialog) {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

/* 确保详情弹窗内容适当大小 */
:deep(.notification-detail-dialog .el-dialog) {
  margin: 0 auto;
  height: auto;
  max-height: 80vh;
  width: 500px;
}

.notification-content {
  max-height: 60vh;
  overflow-y: auto;
}

.notification-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  color: #909399;
}

.loading-icon {
  margin-right: 5px;
  animation: rotating 2s linear infinite;
}

.notification-empty {
  padding: 20px 0;
  text-align: center;
  color: #909399;
}

.notification-list {
  display: flex;
  flex-direction: column;
}

.notification-item {
  padding: 8px 12px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background-color 0.3s;
}

.notification-item:hover {
  background-color: #f5f7fa;
}

.notification-item.notification-unread {
  background-color: #f0f9ff;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-info {
  display: flex;
  flex-direction: column;
}

.notification-title {
  font-weight: 500;
  font-size: 14px;
  color: #303133;
  margin-bottom: 3px;
  display: flex;
  align-items: center;
}

.unread-tag {
  margin-right: 8px;
}

.notification-time {
  font-size: 12px;
  color: #909399;
}

.pagination-container {
  margin-top: 10px;
  display: flex;
  justify-content: center;
}

/* 通知详情样式 */
.notification-detail {
  padding: 8px;
}

.notification-detail-header {
  display: flex;
  margin-bottom: 15px;
}

.notification-detail-info {
  margin-left: 15px;
}

.notification-detail-sender {
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 3px;
}

.notification-detail-time {
  font-size: 12px;
  color: #909399;
}

.notification-detail-content {
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
  line-height: 1.5;
  font-size: 14px;
  color: #333;
  white-space: pre-wrap;
}

/* 滚动条样式 */
.notification-content::-webkit-scrollbar {
  width: 6px;
}

.notification-content::-webkit-scrollbar-thumb {
  background-color: #dcdfe6;
  border-radius: 3px;
}

.notification-content::-webkit-scrollbar-track {
  background-color: #f5f7fa;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style> 